import { createAction } from '@reduxjs/toolkit';
import type { IWhiteLabelConfiguration, IPaymentMethod } from '@shore/types';
import { NOOP } from '../constants';
import createDebug from '../utils/debug';
import type { AppointmentUsage } from '../features/bookings-upgrade/usage';
// CurrentMerchantData and SubscriptionResponse come from shared/types rather
// than @shore/types: the latter is widened there with `limits`, which the
// published package does not model yet.
import type {
  InitializeSuccessPayload,
  MyAccountData,
  SessionPermissionsPayload,
  BookingConfigurations,
  CurrentMerchantData,
  SubscriptionResponse,
} from '../shared/types';

const debug = createDebug('actions');

export interface NotificationCounts {
  appointments: number;
  messages: number;
}

export interface AppointmentNotification {
  id: string;
  appointmentId: string;
  appointmentType: string;
  groupId: string;
  timestamp: string;
  customerName: string;
  status: string;
  requestedDate: string;
  previousDate?: string;
  services: string;
}

export interface DocumentHeaders {
  lastModified: string | null;
  date: string | null;
}

export interface OrganizationStatus {
  show_payment_warning: boolean;
  dunning_warning: string | null;
}

export interface ModalAppPayload {
  name: string | null;
  [key: string]: unknown;
}

export interface ContractDetails {
  packages: string[];
  states: string[];
  paymentSources: string[];
  defaultPaymentSourceType: string | null;
}

// ── Simple action creators ────────────────────────────────────────────────────

export const receiveAvatarUrl = createAction<string>('RECEIVE_AVATAR_URL');
export const closeOverlay = createAction<void>('CLOSE_OVERLAY');
export const closeSidebar = createAction<void>('CLOSE_SIDEBAR');
export const closeModal = createAction<void>('CLOSE_MODAL');
export const hitEscape = createAction<void>('HIT_ESCAPE');
export const storeMerchantId = createAction<string>('STORE_MERCHANT_ID');
export const reloadAppShell = createAction<void>('RELOAD_APP_SHELL');
export const setLoadingApp = createAction<boolean>('SET_LOADING_APP');
export const setLoadingOverlay = createAction<boolean>('SET_LOADING_OVERLAY');
export const setLoadingSidebar = createAction<boolean>('SET_LOADING_SIDEBAR');
export const openModal = createAction<void>('OPEN_MODAL');
export const openAppInModal =
  createAction<ModalAppPayload>('OPEN_APP_IN_MODAL');
export const modalAppLoaded = createAction<{ destroy?: () => void } | null>(
  'MODAL_APP_LOADED'
);
export const clickModalBackground = createAction<void>(
  'CLICK_MODAL_BACKGROUND'
);
export const notificationsAppointmentClick = createAction<void>(
  'NOTIFICATIONS_APPOINTMENT_CLICK'
);
export const notificationsMessageClick = createAction<number>(
  'NOTIFICATIONS_MESSAGE_CLICK'
);
export const initNotificationCounts = createAction<NotificationCounts>(
  'INIT_NOTIFICATION_COUNTS'
);
export const newNotificationCounts = createAction<NotificationCounts>(
  'NEW_NOTIFICATION_COUNTS'
);
export const initNotificationClientMessages = createAction<string[]>(
  'INIT_NOTIFICATION_CLIENT_MESSAGES'
);
export const notificationsClientMessageCloseClick = createAction<string>(
  'NOTIFICATIONS_CLIENT_MESSAGE_CLOSE_CLICK'
);
export const notificationsAppointmentItemClick = createAction<{
  id: string;
  appointmentId: string;
  appointmentType: string;
  groupId: string;
}>('NOTIFICATIONS_APPOINTMENT_ITEM_CLICK');
export const notificationsAppointmentCloseClick = createAction<string>(
  'NOTIFICATIONS_APPOINTMENT_CLOSE_CLICK'
);
export const notificationsAppointmentClearAllClick = createAction<void>(
  'NOTIFICATIONS_APPOINTMENT_CLEAR_ALL_CLICK'
);
export const notificationsAppointmentFetchSuccess = createAction<
  AppointmentNotification[]
>('NOTIFICATIONS_APPOINTMENT_FETCH_SUCCESS');
export const notificationsAppointmentFetchError = createAction<void>(
  'NOTIFICATIONS_APPOINTMENT_FETCH_ERROR'
);
export const dismissAppAndOverlayError = createAction<void>(
  'DISMISS_APP_AND_OVERLAY_ERROR'
);
export const clickHeaderButton = createAction<string>('CLICK_HEADER_BUTTON');
export const receiveDocumentHeaders = createAction<DocumentHeaders>(
  'RECEIVE_DOCUMENT_HEADERS'
);
export const clickLogout = createAction<void>('CLICK_LOGOUT');
export const documentClick = createAction<void>('DOCUMENT_CLICK');
export const setOrganizationStatus = createAction<OrganizationStatus>(
  'SET_ORGANIZATION_STATUS'
);
export const setDefaultPaymentSourceType = createAction<string | null>(
  'SET_DEFAULT_PAYMENT_SOURCE_TYPE'
);
export const setContractDetails = createAction<ContractDetails>(
  'SET_CONTRACT_DETAILS'
);
export const setHubspotReady = createAction<void>('SET_HUBSPOT_READY');
export const setIntercomReady = createAction<void>('SET_INTERCOM_READY');

// ── Error action creators ─────────────────────────────────────────────────────

export interface ErrorActionPayload {
  message: string;
  name: string;
}

// Preserves the raw error in `meta` so Sentry can receive the full Error object.
const createErrorAction = (type: string) =>
  createAction(
    type,
    (error?: unknown, extraTags: Record<string, unknown> = {}) => ({
      payload:
        error instanceof Error
          ? { message: error.message, name: error.name }
          : {
              message: error ? String(error) : 'An unknown error occurred',
              name: 'Error',
            },
      meta: { error, extraTags },
    })
  );

export const initializeError = createErrorAction('INITIALIZE_ERROR');
export const appError = createErrorAction('APP_ERROR');
export const setupBrowserLocaleAndErrorState = createErrorAction(
  'SETUP_BROWSER_LOCALE'
);
export const overlayError = createErrorAction('OVERLAY_ERROR');
export const sidebarError = createErrorAction('SIDEBAR_ERROR');

// ── Actions with payload transformations ──────────────────────────────────────

export const notificationSMSDisplaySeverity = createAction<string>(
  'NOTIFICATIONS_SMS_DISPLAY_SEVERITY'
);

export const updateTrialPeriod = createAction<string>('UPDATE_TRIAL_PERIOD');

export const initializeSuccess = createAction(
  'INITIALIZE_SUCCESS',
  (
    myAccountData: MyAccountData,
    currentMerchantData: CurrentMerchantData,
    whiteLabelConfig: IWhiteLabelConfiguration,
    packageCode: string | null,
    sessionPermissions: SessionPermissionsPayload,
    subscription: SubscriptionResponse | undefined,
    sessionRole: string | null,
    paymentSettings: { payment_methods: IPaymentMethod[] } | null,
    bookingConfigs: BookingConfigurations,
    appointmentUsage: AppointmentUsage | null
  ) => ({
    payload: {
      myAccountData,
      currentMerchantData,
      whiteLabelConfig,
      packageCode,
      sessionPermissions,
      subscription,
      sessionRole,
      paymentSettings,
      bookingConfigs,
      appointmentUsage,
    } satisfies InitializeSuccessPayload,
  })
);

/**
 * Fresh appointment allowance, read over HTTP. The only way the allowance moves
 * after bootstrap — the realtime signal carries no counts, so every trigger
 * arrives here through a read.
 */
export const appointmentUsageFetched = createAction(
  'APPOINTMENT_USAGE_FETCHED',
  (usage: AppointmentUsage) => ({ payload: usage })
);

export const openSidebar = createAction('OPEN_SIDEBAR', (url: string) => ({
  payload: { url },
}));

export const changeLocation = createAction(
  'ROUTING/CHANGE_LOCATION',
  (url: string) => ({ payload: { url } })
);

export const navigateTo = createAction(
  'APP(app-shell)/NAVIGATE_TO',
  (url: string) => ({ payload: { url } })
);

// ── Higher-order action creator ───────────────────────────────────────────────

export interface AppAction {
  type: string;
  meta?: {
    proxy?: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export const proxyAppAction =
  (
    onProxyAction: (action: AppAction) => void = NOOP as (
      action: AppAction
    ) => void
  ) =>
  (appName: string, appAction: AppAction): AppAction | null => {
    if (!appAction.meta || !appAction.meta.proxy) return null;

    const proxiedAction: AppAction = {
      ...appAction,
      type: `APP(${appName})/${appAction.type}`,
      meta: { ...appAction.meta, proxy: false },
    };

    debug('proxyAppAction', proxiedAction);

    onProxyAction(proxiedAction);

    return proxiedAction;
  };
